// Server is authoritative. Local storage keeps only a job reference and an idempotency key for an uncertain POST.
export function isSynchronizationRunning(job) {
  return !!job && (job.status === "QUEUED" || job.status === "RUNNING");
}
function read(key) { try { return JSON.parse(window.localStorage.getItem(key)); } catch (_) { return null; } }
function write(key, value) { try { if (value == null) window.localStorage.removeItem(key); else window.localStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* Server discovery also works without storage. */ } }
function requestKey() { return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2) + "-" + Math.random().toString(36).slice(2); }

export function createDeviceSynchronizationTracker(api, scope) {
  const referenceKey = "recognition-sync:" + scope;
  const intentKey = referenceKey + ":request";
  return {
    data() {
      return { syncJob: null, syncJobs: [], restoringSyncJobs: true, startingSyncJob: false,
        trackingError: "", synchronizationPollTimer: null, synchronizationGeneration: 0,
        synchronizationListRequest: 0, synchronizationDisposed: false, synchronizationPollFailures: 0, synchronizationPollInFlight: null };
    },
    computed: { synchronizationRunning() { return isSynchronizationRunning(this.syncJob); } },
    mounted() { this.refreshSynchronizationJobs(true); },
    beforeDestroy() { this.synchronizationDisposed = true; this.synchronizationGeneration++; this.stopSynchronizationPolling(); },
    methods: {
      stopSynchronizationPolling() {
        if (this.synchronizationPollTimer != null) clearTimeout(this.synchronizationPollTimer);
        this.synchronizationPollTimer = null;
      },
      scheduleSynchronizationPolling() {
        this.stopSynchronizationPolling();
        if (this.synchronizationDisposed || (!this.synchronizationRunning && !this.trackingError)) return;
        if (this.syncJob && this.synchronizationPollInFlight === this.synchronizationGeneration + ":" + this.syncJob.id) return;
        const delay = Math.min(10000, 2000 * Math.max(1, this.synchronizationPollFailures));
        this.synchronizationPollTimer = setTimeout(() => {
          this.synchronizationPollTimer = null;
          if (this.syncJob && (this.synchronizationRunning || !read(intentKey))) this.pollSynchronizationJob(); else this.refreshSynchronizationJobs(true);
        }, delay);
      },
      applySynchronizationJob(job, notify = false) {
        if (!job || !job.id) throw new Error("Không nhận được mã tác vụ đồng bộ");
        const previous = this.syncJob;
        if (previous && previous.id === job.id &&
            ((!isSynchronizationRunning(previous) && isSynchronizationRunning(job)) ||
             (previous.updatedAt && job.updatedAt && previous.updatedAt > job.updatedAt))) return;
        this.syncJob = job;
        this.syncJobs = [job, ...this.syncJobs.filter(item => item.id !== job.id)];
        write(referenceKey, job.id);
        if (notify && !isSynchronizationRunning(job) && this.onSynchronizationFinished) this.onSynchronizationFinished(job);
      },
      selectSynchronizationJob(id) {
        const job = this.syncJobs.find(item => item.id === id);
        if (!job) return;
        this.synchronizationGeneration++; this.stopSynchronizationPolling();
        this.syncJob = null; this.applySynchronizationJob(job);
        this.trackingError = "";
        if (this.onSynchronizationRecovered) this.onSynchronizationRecovered(job);
        this.scheduleSynchronizationPolling();
      },
      async refreshSynchronizationJobs(recover = false) {
        const request = ++this.synchronizationListRequest;
        const generation = this.synchronizationGeneration;
        this.restoringSyncJobs = true;
        try {
          const response = await api.getSynchronizationJobs();
          if (this.synchronizationDisposed || request !== this.synchronizationListRequest || generation !== this.synchronizationGeneration) return;
          const jobs = response.data.data;
          if (!Array.isArray(jobs)) throw new Error("Dữ liệu tiến trình không hợp lệ");
          this.syncJobs = jobs;
          const intent = read(intentKey), remembered = read(referenceKey);
          const recovered = (intent && jobs.find(job => job.requestId === intent.requestId)) ||
            jobs.find(job => job.id === remembered && isSynchronizationRunning(job)) || jobs.find(isSynchronizationRunning) || jobs.find(job => job.id === remembered);
          const current = this.syncJob && jobs.find(job => job.id === this.syncJob.id);
          if (current) this.applySynchronizationJob(current, isSynchronizationRunning(this.syncJob) && !isSynchronizationRunning(current));
          else if (recover && recovered) {
            this.applySynchronizationJob(recovered);
            if (this.onSynchronizationRecovered) this.onSynchronizationRecovered(recovered);
          }
          const pending = intent && !jobs.some(job => job.requestId === intent.requestId);
          if (intent && !pending) write(intentKey, null);
          this.trackingError = pending ? "Đang kiểm tra yêu cầu chưa nhận được phản hồi. Không tạo thêm lượt đồng bộ khi tải lại trang." : "";
          this.synchronizationPollFailures = 0;
        } catch (_) {
          if (!this.synchronizationDisposed && request === this.synchronizationListRequest && generation === this.synchronizationGeneration) {
            this.trackingError = "Chưa lấy được tiến trình. Hệ thống sẽ thử kết nối lại; tác vụ đang chạy không bị hủy.";
            this.synchronizationPollFailures++;
          }
        } finally {
          if (request === this.synchronizationListRequest) this.restoringSyncJobs = false;
          if (generation === this.synchronizationGeneration) this.scheduleSynchronizationPolling();
        }
      },
      async pollSynchronizationJob() {
        if (!this.syncJob || this.synchronizationDisposed) return;
        const id = this.syncJob.id, generation = this.synchronizationGeneration;
        const token = generation + ":" + id;
        if (this.synchronizationPollInFlight === token) return;
        this.stopSynchronizationPolling(); this.synchronizationPollInFlight = token;
        try {
          const response = await api.getSynchronizationJob(id);
          if (this.synchronizationDisposed || generation !== this.synchronizationGeneration || !this.syncJob || this.syncJob.id !== id) return;
          const job = response.data.data;
          if (!job || job.id !== id) throw new Error("Dữ liệu tiến trình không hợp lệ");
          this.applySynchronizationJob(job, isSynchronizationRunning(this.syncJob) && !isSynchronizationRunning(job));
          this.trackingError = ""; this.synchronizationPollFailures = 0;
        } catch (_) {
          if (!this.synchronizationDisposed && generation === this.synchronizationGeneration) {
            this.trackingError = "Mất kết nối theo dõi. Tác vụ vẫn có thể đang chạy; đang thử kết nối lại...";
            this.synchronizationPollFailures++;
          }
        } finally {
          if (this.synchronizationPollInFlight === token) this.synchronizationPollInFlight = null;
          if (generation === this.synchronizationGeneration) this.scheduleSynchronizationPolling();
        }
      },
      async startSynchronizationJob(payload) {
        if (this.startingSyncJob || this.synchronizationRunning || this.restoringSyncJobs) return null;
        const signature = JSON.stringify(payload);
        let intent = read(intentKey);
        if (!intent || intent.signature !== signature) intent = { signature, requestId: requestKey() };
        write(intentKey, intent);
        if (this.syncJob && !this.synchronizationRunning) { this.syncJob = null; write(referenceKey, null); }
        this.startingSyncJob = true;
        try {
          const response = await api.startSynchronizationJob({ ...payload, requestId: intent.requestId });
          if (this.synchronizationDisposed) return null;
          const job = response.data.data;
          this.synchronizationGeneration++; this.stopSynchronizationPolling();
          this.syncJob = null; this.applySynchronizationJob(job, true);
          this.trackingError = ""; this.synchronizationPollFailures = 0; write(intentKey, null);
          if (job.requestId !== intent.requestId && isSynchronizationRunning(job) && this.onSynchronizationAlreadyRunning) this.onSynchronizationAlreadyRunning(job);
          if (this.onSynchronizationRecovered) this.onSynchronizationRecovered(job);
          this.scheduleSynchronizationPolling();
          return job;
        } catch (error) {
          // A lost POST response does not prove that the job was not created. Discover it before offering a retry.
          await this.refreshSynchronizationJobs(true);
          const job = this.syncJobs.find(item => item.requestId === intent.requestId) || this.syncJobs.find(item =>
            isSynchronizationRunning(item) && item.idDeviceNew === payload.idDeviceNew && (!payload.idSchool || item.idSchool === payload.idSchool));
          if (job) {
            write(intentKey, null); this.trackingError = "";
            if (!this.syncJob || this.syncJob.id !== job.id) {
              this.synchronizationGeneration++; this.syncJob = null; this.applySynchronizationJob(job);
              if (this.onSynchronizationRecovered) this.onSynchronizationRecovered(job);
            }
            if (job.requestId !== intent.requestId && this.onSynchronizationAlreadyRunning) this.onSynchronizationAlreadyRunning(job);
            this.scheduleSynchronizationPolling(); return job;
          }
          const status = error && error.response && error.response.status;
          if (status >= 400 && status < 500) { write(intentKey, null); this.trackingError = ""; this.stopSynchronizationPolling(); throw error; }
          this.trackingError = "Chưa xác nhận được yêu cầu đồng bộ. Đang tiếp tục kiểm tra tiến trình, không kết luận tác vụ thất bại.";
          this.scheduleSynchronizationPolling();
          const uncertain = new Error(this.trackingError); uncertain.synchronizationStartUncertain = true; throw uncertain;
        } finally { this.startingSyncJob = false; }
      },
      dismissSynchronizationResult() {
        if (!this.synchronizationRunning && !read(intentKey)) {
          this.synchronizationGeneration++; this.trackingError = "";
          write(referenceKey, null);
          this.syncJob = null; this.stopSynchronizationPolling();
        }
      },
    },
  };
}
